import React, { CSSProperties } from "react";

interface ImageExportProps {
  src?: string;
  alt?: string;
  description?: string;
  width?: string;
  height?: string;
  borderRadius?: number;
  objectFit?: "cover" | "contain" | "fill" | "none";
  align?: "left" | "center" | "right";
  link?: string;
  linkTarget?: "_self" | "_blank";
}

// Default placeholder URLs that should be treated as "not set"
const PLACEHOLDER_URLS = ["https://images.unsplash.com/photo-1576091160550-2173dba999ef"];

const isPlaceholderUrl = (url: string): boolean => {
  return PLACEHOLDER_URLS.some((placeholder) => url.startsWith(placeholder));
};

export const ImageExport: React.FC<ImageExportProps> = ({
  src = "",
  alt = "Image",
  description,
  width = "100%",
  height = "auto",
  borderRadius = 0,
  objectFit = "cover",
  align = "left",
  link,
  linkTarget = "_self",
}) => {
  // Check for valid src - exclude placeholder URLs
  const hasValidSrc = src && src.trim() !== "" && !isPlaceholderUrl(src);

  const imgStyle: CSSProperties = {
    width,
    height,
    borderRadius: `${borderRadius}px`,
    objectFit: objectFit as CSSProperties["objectFit"],
    display: "block",
  };

  const wrapperStyle: CSSProperties = {
    display: "flex",
    justifyContent: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
  };

  const placeholderStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width,
    height: height === "auto" ? "200px" : height,
    minHeight: "120px",
    backgroundColor: "#e5e7eb",
    border: "2px dashed #9ca3af",
    borderRadius: `${borderRadius}px`,
  };

  if (!hasValidSrc) {
    return (
      <div style={wrapperStyle}>
        <div style={placeholderStyle}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21,15 16,10 5,21" />
          </svg>
          <span style={{ fontSize: "0.875rem", color: "#6b7280", marginTop: "8px" }}>Image</span>
          <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>No image configured</span>
        </div>
      </div>
    );
  }

  const imageElement = (
    <img src={src} alt={alt} style={imgStyle} {...(description ? { "data-description": description } : {})} />
  );

  if (link) {
    return (
      <div style={wrapperStyle}>
        <a
          href={link}
          target={linkTarget}
          rel={linkTarget === "_blank" ? "noopener noreferrer" : undefined}
          style={{ display: "block" }}
        >
          {imageElement}
        </a>
      </div>
    );
  }

  return <div style={wrapperStyle}>{imageElement}</div>;
};
